import { useState } from 'react'

import { useEffect } from 'modules/core/hooks/useEffect'

import { useResultValidations } from 'modules/payroll_bundle/hooks/useResultValidations'
import { usePolicyPeriods } from 'modules/payroll_bundle/lib/policyPeriodsContext'
import bankAccounts from 'modules/payroll_bundle/store/collections/bankAccounts'
import paymentItems from 'modules/payroll_bundle/store/collections/paymentItems'
import payments from 'modules/payroll_bundle/store/collections/payments'
import resultsCollection from 'modules/payroll_bundle/store/collections/results'

import { useEmployeeResultsSummaries } from './useEmployeeResultsSummaries'
import { useFetchUploadedResultData } from './useFetchUploadedResultData'
import { useResultEmployees } from './useResultEmployees'

export const useResultsPageLoader = () => {
  const [fetching, setFetching] = useState(true)
  const [fetchingBankAccounts, setFetchingBankAccounts] = useState(true)
  const { loadingPolicyPeriodCollection, currentPeriod, prevPeriod } = usePolicyPeriods()
  const { fetch: fetchUploadedResultData } = useFetchUploadedResultData()
  const currentPeriodId = currentPeriod?.id
  const previousPeriodId = prevPeriod?.id

  const { fetching: summariesFetching } = useEmployeeResultsSummaries(currentPeriodId)

  const { loading: loadingResultEmployees } = useResultEmployees(currentPeriodId)

  // This data is used to show the validations banner stating how many employees have results in warning or invalid states.
  // This should be a query for the summary/report, not for the individual validations
  const { data } = useResultValidations(currentPeriodId)

  useEffect(() => {
    fetchBankAccounts()
  }, [])

  useEffect(() => {
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previousPeriodId, currentPeriodId])

  async function fetchBankAccounts() {
    try {
      setFetchingBankAccounts(true)
      await bankAccounts.fetch()
    } finally {
      setFetchingBankAccounts(false)
    }
  }

  async function fetchData() {
    try {
      if (currentPeriod) {
        setFetching(true)

        await Promise.all([
          resultsCollection.fetch({
            data: { policy_period_ids: [currentPeriodId, previousPeriodId] },
          }),
          payments.fetch({ data: { policy_period_id: currentPeriodId } }),
          paymentItems.fetch({ data: { policy_period_id: currentPeriodId } }),
          fetchUploadedResultData(currentPeriodId),
        ])
      }
    } finally {
      setFetching(false)
    }
  }

  return {
    fetching:
      fetching ||
      loadingPolicyPeriodCollection ||
      fetchingBankAccounts ||
      summariesFetching ||
      loadingResultEmployees,
    resultValidations: data,
    hasResults: resultsCollection.filterByPolicyPeriod(currentPeriodId).length > 0,
    fetchData: fetchData,
  }
}
